import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Save, CheckSquare, Square, ChevronDown, ChevronUp } from 'lucide-react';

const DEFAULT_ITEMS = [
  { key: 'behavior', label: 'Behavior interventions/supports considered', checked: false, narrative: '' },
  { key: 'language_needs', label: 'Language needs for English learners', checked: false, narrative: '' },
  { key: 'braille', label: 'Braille / visually impaired considerations', checked: false, narrative: '' },
  { key: 'communication', label: 'Communication needs', checked: false, narrative: '' },
  { key: 'peer_communication', label: 'Opportunities for direct communication with peers', checked: false, narrative: '' },
  { key: 'assistive_tech', label: 'Assistive technology devices/services', checked: false, narrative: '' },
  { key: 'health', label: 'Health concerns', checked: false, narrative: '' },
];

function mergeContent(existing) {
  const defaults = { items: DEFAULT_ITEMS.map(i => ({ ...i })) };
  if (!existing || typeof existing !== 'object') return defaults;
  if (existing.notes && !existing.items) {
    return defaults;
  }
  if (existing.items && Array.isArray(existing.items)) {
    // Merge with defaults to pick up any new items
    const existingKeys = new Set(existing.items.map(i => i.key));
    const merged = existing.items.map(i => ({ ...DEFAULT_ITEMS.find(d => d.key === i.key) || {}, ...i }));
    DEFAULT_ITEMS.forEach(d => {
      if (!existingKeys.has(d.key)) merged.push({ ...d });
    });
    return { items: merged };
  }
  return defaults;
}

export default function ConsiderationsEditor({ section, caseData, onSave, saving }) {
  const [content, setContent] = useState(() => mergeContent(section?.content));
  const [expanded, setExpanded] = useState({});

  useEffect(() => {
    setContent(mergeContent(section?.content));
  }, [section?.section_id]);

  const toggleCheck = (index) => {
    setContent(prev => ({
      ...prev,
      items: prev.items.map((item, i) => i === index ? { ...item, checked: !item.checked } : item),
    }));
  };

  const updateNarrative = (index, value) => {
    setContent(prev => ({
      ...prev,
      items: prev.items.map((item, i) => i === index ? { ...item, narrative: value } : item),
    }));
  };

  const toggleExpand = (key) => {
    setExpanded(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const handleSave = () => onSave(content);
  const checkedCount = content.items.filter(i => i.checked).length;

  return (
    <div className="max-w-4xl space-y-6" data-testid="considerations-editor">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="font-outfit text-xl font-semibold text-[#1C1C1C]" data-testid="considerations-title">Considerations</h2>
          <p className="text-sm text-[#595959] mt-1">{section?.description}</p>
          <p className="text-xs text-[#3B6A9B] mt-2">{checkedCount} of {content.items.length} considerations addressed</p>
        </div>
        <Button onClick={handleSave} disabled={saving} className="bg-[#3B6A9B] hover:bg-[#2D5480] text-white rounded-xl shrink-0" data-testid="considerations-save-top">
          <Save className="h-4 w-4 mr-2" />{saving ? 'Saving...' : 'Save'}
        </Button>
      </div>

      <div className="space-y-3">
        {content.items.map((item, idx) => {
          const isExpanded = expanded[item.key] || item.checked;
          return (
            <div key={item.key} className={`bg-white border rounded-2xl transition-all ${item.checked ? 'border-[#3B6A9B]/30' : 'border-[#E5E5E5]'}`} data-testid={`consideration-${item.key}`}>
              <div className="flex items-center gap-3 px-5 py-4 cursor-pointer" onClick={() => toggleCheck(idx)}>
                <button className="shrink-0" data-testid={`check-${item.key}`}>
                  {item.checked ? <CheckSquare className="h-5 w-5 text-[#3B6A9B]" /> : <Square className="h-5 w-5 text-[#E5E5E5]" />}
                </button>
                <span className={`text-sm flex-1 ${item.checked ? 'text-[#1C1C1C] font-medium' : 'text-[#595959]'}`}>{item.label}</span>
                <button onClick={e => { e.stopPropagation(); toggleExpand(item.key); }} className="p-1 rounded hover:bg-gray-100" data-testid={`expand-${item.key}`}>
                  {isExpanded ? <ChevronUp className="h-4 w-4 text-[#595959]" /> : <ChevronDown className="h-4 w-4 text-[#595959]" />}
                </button>
              </div>
              {isExpanded && (
                <div className="px-5 pb-4 border-t border-[#F3F4F1]">
                  <p className="text-xs text-[#595959] mt-3 mb-2">
                    {item.checked ? 'Describe the consideration and how it is addressed:' : 'Mark as applicable above, then provide details:'}
                  </p>
                  <Textarea
                    value={item.narrative}
                    onChange={e => updateNarrative(idx, e.target.value)}
                    placeholder="Provide clarification, description, or narrative..."
                    rows={3}
                    className="rounded-xl border-[#E5E5E5] text-sm"
                    data-testid={`narrative-${item.key}`}
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="flex justify-end pt-4 border-t border-[#E5E5E5]">
        <Button onClick={handleSave} disabled={saving} className="bg-[#3B6A9B] hover:bg-[#2D5480] text-white rounded-xl" data-testid="considerations-save-bottom">
          <Save className="h-4 w-4 mr-2" />{saving ? 'Saving...' : 'Save Considerations'}
        </Button>
      </div>
    </div>
  );
}
